import { authApi } from "@/api/auth-api";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { setPostHogGroup } from "@/lib/posthog";

export interface WorkspaceStatus {
  colour: string;
  title: string;
  description: string;
  order: number;
}

interface Workspace {
  _id: string;
  opportunity_name: string;
  created_on: string;
  statuses: WorkspaceStatus[];
  deal_count?: number;
  role?: string;
  affiliated_company?: {
    _id: string;
    company_name: string;
  } | null;
  portal_slug?: string;
  raise_board_enabled?: boolean;
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd?: string;
  };
}

interface WorkspaceStore {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setActiveWorkspaceId: (id: string) => void;
  activeWorkspace: Workspace | null;
  fetchWorkspaces: () => Promise<void>;
  isInitialized: boolean;
  isInitializing: boolean;
  initializeWorkspaces: () => Promise<void>;
  getStatusColor: (status: string) => string;
  updateWorkspace: (workspace: Workspace) => void;
  updateSubscription: (subscription: Workspace["subscription"]) => void;
}

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      workspaces: [],
      activeWorkspaceId: null,
      activeWorkspace: null,
      isInitialized: false,
      isInitializing: false,

      updateWorkspace: (workspace) => {
        set((state) => {
          const updatedWorkspaces = state.workspaces.map((w) =>
            w._id === workspace._id ? workspace : w
          );

          return {
            workspaces: updatedWorkspaces,
            activeWorkspace:
              state.activeWorkspaceId === workspace._id
                ? workspace
                : state.activeWorkspace,
          };
        });
      },

      updateSubscription: (subscription) => {
        set((state) => {
          if (!state.activeWorkspace) return state;

          const updatedWorkspace = {
            ...state.activeWorkspace,
            subscription,
          };

          const updatedWorkspaces = state.workspaces.map((w) =>
            w._id === updatedWorkspace._id ? updatedWorkspace : w
          );

          return {
            workspaces: updatedWorkspaces,
            activeWorkspace: updatedWorkspace,
          };
        });
      },

      getStatusColor: (status: string) => {
        const workspace = get().activeWorkspace;
        const statusObj = workspace?.statuses.find((s) => s.title === status);
        if (statusObj?.colour?.startsWith("#")) {
          return `style="background-color: ${statusObj.colour}; color: white"`;
        }
        return "bg-gray-100 text-gray-800";
      },

      initializeWorkspaces: async () => {
        const state = get();
        console.log("🔍 WorkspaceStore: initializeWorkspaces called", {
          isInitialized: state.isInitialized,
          isInitializing: state.isInitializing,
        });
        if (state.isInitialized) {
          console.log("🔍 WorkspaceStore: Already initialized, skipping");
          return;
        }

        // Prevent multiple simultaneous initializations
        if (state.isInitializing) {
          console.log("🔍 WorkspaceStore: Already initializing, skipping");
          return;
        }

        try {
          console.log(
            "🔍 WorkspaceStore: Starting workspace initialization..."
          );
          set({ isInitializing: true });
          await state.fetchWorkspaces();
          set({ isInitialized: true, isInitializing: false });
          console.log("🔍 WorkspaceStore: Workspaces initialized successfully");
        } catch (error) {
          console.error(
            "❌ WorkspaceStore: Failed to initialize workspaces:",
            error
          );
          set({ isInitializing: false });
          throw error;
        }
      },

      setWorkspaces: (workspaces) => {
        const { activeWorkspaceId } = get();
        // Ensure workspaces is an array
        const workspaceArray = workspaces || [];
        const normalizedWorkspaces = workspaceArray.map((w) => ({
          ...w,
          statuses: w.statuses || [],
        }));

        // Try to find the persisted workspace first, then fall back to first workspace
        const persistedWorkspace = activeWorkspaceId
          ? normalizedWorkspaces.find((w) => w._id === activeWorkspaceId)
          : null;

        const newActiveWorkspace =
          persistedWorkspace || normalizedWorkspaces[0] || null;

        // Set PostHog group for workspace analytics
        if (newActiveWorkspace) {
          setPostHogGroup("workspace", newActiveWorkspace._id, {
            name: newActiveWorkspace.opportunity_name,
          });
        }

        set({
          workspaces: normalizedWorkspaces,
          activeWorkspaceId: newActiveWorkspace?._id || null,
          activeWorkspace: newActiveWorkspace,
        });
      },

      setActiveWorkspaceId: (id) => {
        set((state) => {
          const newActiveWorkspace = state.workspaces.find((w) => w._id === id);

          // Set PostHog group for workspace analytics
          if (newActiveWorkspace) {
            setPostHogGroup("workspace", newActiveWorkspace._id, {
              name: newActiveWorkspace.opportunity_name,
            });
          }

          return {
            activeWorkspaceId: id,
            activeWorkspace: newActiveWorkspace || null,
          };
        });
      },

      fetchWorkspaces: async () => {
        try {
          console.log("🔍 Fetching workspaces...");
          const workspaces = await authApi.getWorkspaces();
          console.log("🔍 Workspaces fetched:", workspaces);
          const { activeWorkspaceId } = get();

          // Ensure workspaces is an array
          const workspaceArray = workspaces || [];
          const normalizedWorkspaces = workspaceArray.map((w) => ({
            ...w,
            statuses: w.statuses || [],
          }));

          // Try to find the persisted workspace first, then fall back to first workspace
          const persistedWorkspace = activeWorkspaceId
            ? normalizedWorkspaces.find((w) => w._id === activeWorkspaceId)
            : null;

          const newActiveWorkspace =
            persistedWorkspace || normalizedWorkspaces[0] || null;

          // Set PostHog group for workspace analytics
          if (newActiveWorkspace) {
            setPostHogGroup("workspace", newActiveWorkspace._id, {
              name: newActiveWorkspace.opportunity_name,
            });
          }

          set({
            workspaces: normalizedWorkspaces,
            activeWorkspaceId: newActiveWorkspace?._id || null,
            activeWorkspace: newActiveWorkspace,
          });
        } catch (error) {
          console.error("Failed to fetch workspaces:", error);
          throw error;
        }
      },
    }),
    {
      name: "workspace-storage",
      partialize: (state) => ({ activeWorkspaceId: state.activeWorkspaceId }),
    }
  )
);

// Custom hook to fetch and manage workspaces
export function useWorkspaces() {
  const { setWorkspaces } = useWorkspaceStore();

  const query = useQuery<Workspace[]>({
    queryKey: ["workspaces"],
    queryFn: async () => {
      const workspaces = await authApi.getWorkspaces();
      // Ensure workspaces is an array
      const workspaceArray = workspaces || [];
      return workspaceArray.map((w) => ({
        ...w,
        statuses: (w.statuses || []).map((status: WorkspaceStatus) => ({
          title: status.title,
          colour: status.colour,
          description: status.description,
          order: status.order,
        })),
      })) as unknown as Workspace[];
    },
  });

  // Update workspaces when query succeeds
  useEffect(() => {
    if (query.data) {
      setWorkspaces(query.data);
    }
  }, [query.data, setWorkspaces]);

  return {
    ...query,
    ...useWorkspaceStore(),
  };
}
